import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getDataPromise } from 'brock-fetch-utils';
import { AnimatedPageLoader } from 'smartsuite-ui';

const FeatureTokenContext = React.createContext();

export function isFeatureConditionValid(conditionConfig) {
    return !!conditionConfig;
}

export function isFeatureConditionMet(userTokens, conditionConfig) {
    if (conditionConfig?.condition === 'all') {
        return conditionConfig?.tokens.every((token) =>
            userTokens.includes(token)
        );
    }

    if (conditionConfig?.condition === 'none') {
        return conditionConfig?.tokens.every(
            (token) => !userTokens.includes(token)
        );
    }

    return conditionConfig?.tokens.some((token) => userTokens.includes(token));
}

export function useFeatureTokenContext() {
    const context = useContext(FeatureTokenContext);

    if (!context) {
        throw new Error(
            'featureTokenContext must be used within a FeatureTokenContextProvider. Wrap a parent component in <FeatureTokenContextProvider to fix this error'
        );
    }

    return context;
}

export function FeatureTokenContextProvider({
    children,
    getUserFeatureTokensUri,
    ssiSystemFeature,
}) {
    const [userTokens, setUserTokens] = useState([]);
    const [apiCallSucceeded, setApiCallSucceeded] = useState();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!getUserFeatureTokensUri) {
            // eslint-disable-next-line no-console
            console.error(
                'No URI passed in for getUserFeatureTokensUri. Conditional features are turned off.'
            );
            setIsLoading(false);
        } else {
            getDataPromise(getUserFeatureTokensUri)
                .then(
                    (response) => {
                        setUserTokens(response);
                        setApiCallSucceeded(true);
                    },
                    (error) => {
                        // eslint-disable-next-line no-console
                        console.error(
                            `Error calling feature token api. Conditional features are turned off. ${error}`
                        );
                        setApiCallSucceeded(false);
                    }
                )
                .then(() => setIsLoading(false));
        }
    }, [getUserFeatureTokensUri]);

    if (isLoading) {
        return <AnimatedPageLoader />;
    }

    return (
        <FeatureTokenContext.Provider
            value={{
                isFeatureConditionMet: (conditionConfig) =>
                    apiCallSucceeded
                        ? isFeatureConditionMet(userTokens, conditionConfig)
                        : false,
                isFeatureConditionValid,
                ssiSystemFeature,
            }}
        >
            {children}
        </FeatureTokenContext.Provider>
    );
}

FeatureTokenContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
    getUserFeatureTokensUri: PropTypes.string,
    ssiSystemFeature: PropTypes.shape({
        header: PropTypes.bool,
        footer: PropTypes.bool,
    }),
};
